import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Calendar, Clock, Plus, X, CheckCircle2, AlertCircle, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { useRealtime } from "@/hooks/useRealtime";

const STATUS_STYLES = {
  requested:   { label: "Requested",   badge: "bg-amber-100 text-amber-800",  icon: Clock },
  approved:    { label: "Confirmed",   badge: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  rescheduled: { label: "Rescheduled", badge: "bg-sky-100 text-sky-700",      icon: CalendarDays },
  completed:   { label: "Completed",   badge: "bg-slate-200 text-slate-700",  icon: CheckCircle2 },
  cancelled:   { label: "Cancelled",   badge: "bg-red-100 text-red-700",      icon: X },
  no_show:     { label: "No-show",     badge: "bg-orange-100 text-orange-700", icon: AlertCircle },
};

function todayStr(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

export default function ClientMeetings() {
  const [meta, setMeta] = useState({ reasons: [], statuses: [] });
  const [meetings, setMeetings] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/client/meetings");
      setMeetings(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/meetings/meta").then(({ data }) => setMeta(data)).catch(() => {});
    load();
  }, []);

  // Live updates — when admin approves/reschedules/cancels, refresh list
  useRealtime((evt) => {
    if (evt?.type === "notification" && evt.data?.kind?.startsWith("meeting_")) {
      load();
      toast(evt.data.title, { description: evt.data.body });
    }
  });

  const upcoming = useMemo(
    () => meetings.filter((m) => !["cancelled", "completed", "no_show"].includes(m.status)),
    [meetings],
  );
  const past = useMemo(
    () => meetings.filter((m) => ["cancelled", "completed", "no_show"].includes(m.status)),
    [meetings],
  );

  const cancelMeeting = async (id) => {
    if (!window.confirm("Cancel this meeting request?")) return;
    try {
      await api.post(`/client/meetings/${id}/cancel`);
      toast.success("Meeting cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Schedule</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900">Meetings</h1>
          <p className="text-slate-500 mt-1">Book time with your specialist and track every conversation.</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600 text-white h-11 rounded-xl px-5" onClick={() => setOpen(true)} data-testid="new-meeting-btn">
          <Plus size={18} className="mr-1" /> Schedule meeting
        </Button>
      </div>

      <div className="grid gap-6">
        <MeetingsList
          title="Upcoming"
          empty="No upcoming meetings — schedule one to get started."
          items={upcoming}
          loading={loading}
          onCancel={cancelMeeting}
        />
        <MeetingsList
          title="Past"
          empty="No past meetings yet."
          items={past}
          loading={loading}
        />
      </div>

      <ScheduleDialog open={open} onOpenChange={setOpen} reasons={meta.reasons} onCreated={() => { setOpen(false); load(); }} />
    </AppShell>
  );
}

function MeetingsList({ title, items, empty, loading, onCancel }) {
  return (
    <Card className="p-6 rounded-2xl border-slate-100">
      <div className="flex items-center gap-2 mb-4">
        <Calendar size={18} className="text-amber-500" />
        <p className="font-display font-black text-xl text-slate-900">{title}</p>
        <Badge variant="outline" className="ml-1 rounded-full text-xs">{items.length}</Badge>
      </div>
      {loading ? (
        <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center" data-testid={`meetings-empty-${title.toLowerCase()}`}>{empty}</p>
      ) : (
        <ul className="space-y-3">
          {items.map((m) => {
            const s = STATUS_STYLES[m.status] || STATUS_STYLES.requested;
            const Icon = s.icon;
            const dt = new Date(m.scheduled_at);
            return (
              <li key={m.id} className="rounded-2xl border border-slate-100 p-4 flex items-start gap-4 hover:border-amber-200 transition" data-testid={`meeting-row-${m.id}`}>
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-100 to-amber-50 flex flex-col items-center justify-center shrink-0">
                  <span className="text-[9px] uppercase tracking-widest font-bold text-amber-600">{dt.toLocaleString(undefined, { month: "short" })}</span>
                  <span className="font-display text-xl font-black text-amber-700 leading-none">{dt.getDate()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900">{m.reason}</p>
                    <Badge className={`${s.badge} rounded-full font-semibold gap-1`}>
                      <Icon size={11} /> {s.label}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    <Clock size={11} className="inline mr-1" />
                    {dt.toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })}
                    {m.duration_minutes ? ` · ${m.duration_minutes} min` : ""}
                  </p>
                  {m.client_notes && <p className="text-sm text-slate-600 mt-2 italic">&ldquo;{m.client_notes}&rdquo;</p>}
                  {m.assigned_team_member_name && (
                    <p className="text-xs text-emerald-700 mt-2 font-semibold">With {m.assigned_team_member_name}</p>
                  )}
                </div>
                {onCancel && !["completed", "cancelled", "no_show"].includes(m.status) && (
                  <Button size="sm" variant="ghost" onClick={() => onCancel(m.id)} className="text-red-500 hover:bg-red-50" data-testid={`meeting-cancel-${m.id}`}>
                    <X size={14} className="mr-1" /> Cancel
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function ScheduleDialog({ open, onOpenChange, reasons, onCreated }) {
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(todayStr(1));
  const [slots, setSlots] = useState([]);
  const [pickedIso, setPickedIso] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason(""); setPickedIso(""); setNotes(""); setDate(todayStr(1)); setSlots([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !date) return;
    setLoadingSlots(true);
    api.get("/meetings/slots", { params: { date } })
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
    setPickedIso("");
  }, [date, open]);

  const submit = async () => {
    if (!reason) { toast.error("Please pick a reason"); return; }
    if (!pickedIso) { toast.error("Please pick a time slot"); return; }
    setBusy(true);
    try {
      await api.post("/meetings", { reason, scheduled_at: pickedIso, notes });
      toast.success("Meeting requested — the team will confirm shortly.");
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-meeting-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Schedule a meeting</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">What&rsquo;s this about?</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="meeting-reason-select"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
              <SelectContent>
                {(reasons || []).map((r) => (
                  <SelectItem key={r} value={r} data-testid={`reason-opt-${r.replace(/[^a-z]/gi, "-").toLowerCase()}`}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Pick a date</Label>
            <Input type="date" value={date} min={todayStr(0)} onChange={(e) => setDate(e.target.value)} data-testid="meeting-date-input" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Available times</Label>
            {loadingSlots ? (
              <p className="text-sm text-slate-400 py-4">Loading slots…</p>
            ) : slots.length === 0 ? (
              <p className="text-sm text-slate-400 py-4" data-testid="no-slots">No slots available — pick another business day (Mon-Fri).</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2 max-h-64 overflow-y-auto" data-testid="slot-grid">
                {slots.map((s) => (
                  <button
                    key={s.iso}
                    type="button"
                    disabled={!s.available}
                    onClick={() => setPickedIso(s.iso)}
                    data-testid={`slot-${s.iso}`}
                    className={`px-3 py-2 rounded-lg text-sm font-semibold border transition ${
                      pickedIso === s.iso
                        ? "bg-amber-500 text-white border-amber-500"
                        : s.available
                          ? "bg-white border-slate-200 hover:border-amber-300 text-slate-700"
                          : "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Notes for the team (optional)</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the team should know ahead of time…" data-testid="meeting-notes-input" />
          </div>

          <div className="pt-2 flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !reason || !pickedIso} className="bg-amber-500 hover:bg-amber-600" data-testid="meeting-submit">
              {busy ? "Submitting…" : "Request meeting"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
